import {
  color,
  CopySvg,
  FlagReportSvg,
  UrlSvg,
  useColorMode,
  useDisclosure,
  useToast,
} from 'deepstash-ui';
import React from 'react';
import Dropdown, { DropdownEntry } from 'src/components/dropdown/Dropdown';
import ReportModal from 'src/components/modals/report-modal/ReportModal';
import useAuth from 'src/providers/hooks/useAuth';
import { Analytics, Events } from 'src/services/analytics';
import { Idea, Source } from 'types/models';
import { EntityReportType } from 'types/types';
import { TOAST_TIME } from 'utils/constants';
import { handleHtmlToText, slugify } from 'utils/global';
import toastStrings from 'utils/strings/toastStrings';

interface IdeaCardDropdown {
  idea: Idea;
  source: Source | null;
}

const IdeaCardDropdown: React.FC<IdeaCardDropdown> = ({ idea, source }) => {
  const { id, content } = idea;
  const { colorMode } = useColorMode();
  const toast = useToast();

  //Dispatch is an empty functions if we are not on the article page
  //DeleteIdea will make the delete api call even without the Source Provider.
  //So don't call at the same time with deleteIdea from activity

  //Report modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isLoggedIn } = useAuth();

  //This idea is the main idea on an idea page.
  const onCopyLink = () => {
    if (id !== -1) {
      Analytics.logEvent({
        eventName: Events.sharing.shareContent,
        properties: {
          id: id,
          contentType: 'idea',
          type: 'link',
        },
        platforms: ['amplitude'],
      });
    }
    if (source) {
      navigator.clipboard
        .writeText(
          `https://deepstash.com/article/${source.id}/${slugify(
            source.title,
          )}#idea_${idea.id}`,
        )
        .then(() => {
          toast({
            title: toastStrings.linkCopied,
            description: 'The link to the idea has been successfully copied.',
            status: 'success',
            duration: TOAST_TIME,
            isClosable: true,
          });
        });
    }
  };

  const onCopyContent = () => {
    if (id !== -1) {
      Analytics.logEvent({
        eventName: Events.sharing.shareContent,
        properties: {
          id: id,
          contentType: 'idea',
          type: 'text',
        },
        platforms: ['amplitude'],
      });
    }
    navigator.clipboard.writeText(handleHtmlToText(content)).then(() => {
      toast({
        title: toastStrings.contentCopied,
        description: 'The content has been successfully copied.',
        status: 'success',
        duration: TOAST_TIME,
        isClosable: true,
      });
    });
  };

  const items: DropdownEntry[] = [
    {
      title: 'Copy text',
      callback: onCopyContent,
      icon: <CopySvg color={color[colorMode].textSecondary} />,
    },
    {
      title: 'Copy link',
      callback: onCopyLink,
      icon: <UrlSvg color={color[colorMode].textSecondary} />,
    },
  ];

  //Add the report button if SI
  const itemsPublicIdea: DropdownEntry[] = isLoggedIn
    ? [
        ...items,
        {
          title: 'Report',
          callback: onOpen,
          icon: <FlagReportSvg color={color.failure.default} />,
          titleStyling: {
            color: color.failure.default,
          },
        },
      ]
    : items;

  return (
    <>
      <Dropdown items={itemsPublicIdea} />
      {isOpen && source && (
        <ReportModal
          isOpen={isOpen}
          onClose={onClose}
          id={id}
          resourceOwner={source.userData.id}
          type={'idea' as EntityReportType}
        />
      )}
    </>
  );
};

export default IdeaCardDropdown;
